import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Slider } from './ui/slider';
import { Loader2, ImagePlus, Sparkles, Globe, Users, Type, LayoutGrid, CircleDot } from 'lucide-react';
import { toast } from 'sonner';
import { postsApi, VERSE_GRADIENTS, renderVerseImage, mediaUrl } from '../services/postsApi';
import { storiesApi } from '../services/storiesApi';

const PHOTO_FILTERS = [
  { id: 'normal', name: 'Normal', css: 'none' },
  { id: 'clarendon', name: 'Clarendon', css: 'contrast(1.2) saturate(1.35)' },
  { id: 'gingham', name: 'Gingham', css: 'brightness(1.05) sepia(0.15) contrast(0.9)' },
  { id: 'warm', name: 'Warm', css: 'sepia(0.35) saturate(1.4) brightness(1.05)' },
  { id: 'cool', name: 'Cool', css: 'saturate(1.1) hue-rotate(20deg) brightness(1.05)' },
  { id: 'bw', name: 'B&W', css: 'grayscale(1) contrast(1.1)' },
  { id: 'sepia', name: 'Sepia', css: 'sepia(0.7) contrast(1.05)' },
  { id: 'vintage', name: 'Vintage', css: 'sepia(0.45) contrast(1.1) brightness(0.95) saturate(1.3)' },
];

// Bake the selected CSS filter into a new image blob via canvas.
const scaleFilter = (css, strength) => {
  if (!css || css === 'none') return 'none';
  const k = Math.max(0, Math.min(1, strength / 100));
  return css.replace(/([a-z-]+)\(([-\d.]+)(deg)?\)/g, (_, name, val, unit) => {
    const identity = ['brightness', 'contrast', 'saturate'].includes(name) ? 1 : 0;
    const scaled = identity + (parseFloat(val) - identity) * k;
    return `${name}(${Math.round(scaled * 1000) / 1000}${unit || ''})`;
  });
};

const applyPhotoFilter = (file, css) => new Promise((resolve) => {
  if (!css || css === 'none') { resolve(file); return; }
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    ctx.filter = css;
    ctx.drawImage(img, 0, 0);
    canvas.toBlob((b) => resolve(b || file), 'image/jpeg', 0.92);
  };
  img.onerror = () => resolve(file);
  img.src = URL.createObjectURL(file);
});

const CreatePostModal = ({
  open, onClose, onCreated, onStoryCreated, language = 'en',
  defaultDestination = 'feed', allowDestinationChoice = true,
  initialTab, initialVerseText, initialReference, initialImageBlob,
}) => {
  const es = language === 'es';
  const [destination, setDestination] = useState(defaultDestination);
  const [tab, setTab] = useState(initialTab || 'photo');
  const [caption, setCaption] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [submitting, setSubmitting] = useState(false);

  // photo
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFilter, setPhotoFilter] = useState(PHOTO_FILTERS[0]);
  const [filterStrength, setFilterStrength] = useState(100);
  const fileRef = useRef(null);

  // verse / text image
  const [verseText, setVerseText] = useState('');
  const [reference, setReference] = useState('');
  const [gradient, setGradient] = useState(VERSE_GRADIENTS[0]);
  const previewCanvasRef = useRef(null);

  // Sync entry props whenever the modal opens.
  useEffect(() => {
    if (!open) return;
    setDestination(defaultDestination);
    setTab(initialTab || 'photo');
    if (initialVerseText !== undefined) setVerseText(initialVerseText || '');
    if (initialReference !== undefined) setReference(initialReference || '');
    if (initialImageBlob) {
      const f = new File([initialImageBlob], 'recap.png', { type: initialImageBlob.type || 'image/png' });
      setPhotoFile(f);
      setPhotoPreview(URL.createObjectURL(initialImageBlob));
      setPhotoFilter(PHOTO_FILTERS[0]);
      setFilterStrength(100);
    }
  }, [open, defaultDestination, initialTab, initialVerseText, initialReference, initialImageBlob]);

  const isTextual = tab === 'verse' || tab === 'text';
  useEffect(() => {
    if (!isTextual || !previewCanvasRef.current) return;
    (async () => {
      const ref = tab === 'verse' ? (reference || 'John 3:16') : '';
      const blob = await renderVerseImage(verseText || (es ? 'Tu texto aquí…' : 'Your text here…'), ref, gradient);
      const url = URL.createObjectURL(blob);
      previewCanvasRef.current.src = url;
    })();
  }, [tab, isTextual, verseText, reference, gradient, es]);

  const reset = () => {
    setCaption(''); setVisibility('public'); setPhotoFile(null); setPhotoPreview(null);
    setPhotoFilter(PHOTO_FILTERS[0]);
    setFilterStrength(100);
    setVerseText(''); setReference(''); setGradient(VERSE_GRADIENTS[0]); setTab('photo');
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 12 * 1024 * 1024) { toast.error(es ? 'Imagen muy grande (máx 12MB)' : 'Image too large (max 12MB)'); return; }
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
    setPhotoFilter(PHOTO_FILTERS[0]);
    setFilterStrength(100);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      let imageUrl;
      let kind;
      let verseRef = null;
      if (tab === 'photo') {
        if (!photoFile) { toast.error(es ? 'Elige una foto' : 'Pick a photo'); setSubmitting(false); return; }
        const filtered = await applyPhotoFilter(photoFile, scaleFilter(photoFilter.css, filterStrength));
        const up = await postsApi.uploadImage(filtered, photoFile.name.replace(/\.[^.]+$/, '') + '.jpg');
        imageUrl = up.image_url; kind = 'photo';
      } else {
        if (!verseText.trim()) { toast.error(es ? 'Escribe el texto' : 'Enter the text'); setSubmitting(false); return; }
        const ref = tab === 'verse' ? (reference || '') : '';
        const blob = await renderVerseImage(verseText, ref, gradient);
        const up = await postsApi.uploadImage(blob, `${tab}.png`);
        imageUrl = up.image_url;
        verseRef = ref || null;
        // Feed posts only accept verse_image | photo; a text card is a verse_image with no reference.
        kind = destination === 'story' && tab === 'text' ? 'text' : 'verse_image';
      }
      const payload = { kind, image_url: imageUrl, caption, verse_reference: verseRef, visibility };
      if (destination === 'story') {
        const res = await storiesApi.create(payload);
        toast.success(es ? '¡Historia publicada!' : 'Story posted!');
        onStoryCreated?.(res.story);
      } else {
        const res = await postsApi.create(payload);
        toast.success(es ? '¡Publicado!' : 'Posted!');
        onCreated?.(res.post);
      }
      reset();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (es ? 'No se pudo publicar' : 'Could not share'));
    } finally {
      setSubmitting(false);
    }
  };

  const isStory = destination === 'story';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto min-w-0" data-testid="create-post-modal">
        <DialogHeader>
          <DialogTitle>{isStory ? (es ? 'Crear historia' : 'Create a story') : (es ? 'Crear publicación' : 'Create a post')}</DialogTitle>
          <DialogDescription className="sr-only">{es ? 'Comparte una foto, una imagen de versículo o una tarjeta de texto.' : 'Share a photo, verse image or text card.'}</DialogDescription>
        </DialogHeader>

        {allowDestinationChoice && (
          <div className="flex rounded-full bg-muted p-1 w-full" data-testid="create-destination-toggle">
            <button onClick={() => setDestination('feed')}
              className={`flex-1 px-3 py-1.5 rounded-full text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${!isStory ? 'bg-background shadow' : 'text-muted-foreground'}`}
              data-testid="create-destination-feed"><LayoutGrid className="w-4 h-4" /> {es ? 'Feed' : 'Feed'}</button>
            <button onClick={() => setDestination('story')}
              className={`flex-1 px-3 py-1.5 rounded-full text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${isStory ? 'bg-background shadow' : 'text-muted-foreground'}`}
              data-testid="create-destination-story"><CircleDot className="w-4 h-4" /> {es ? 'Historia' : 'Story'}</button>
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab} className="min-w-0 w-full">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="photo" data-testid="create-post-photo-tab"><ImagePlus className="w-4 h-4 mr-1" /> {es ? 'Foto' : 'Photo'}</TabsTrigger>
            <TabsTrigger value="verse" data-testid="create-post-verse-tab"><Sparkles className="w-4 h-4 mr-1" /> {es ? 'Versículo' : 'Verse'}</TabsTrigger>
            <TabsTrigger value="text" data-testid="create-post-text-tab"><Type className="w-4 h-4 mr-1" /> {es ? 'Texto' : 'Text'}</TabsTrigger>
          </TabsList>

          <TabsContent value="photo" className="space-y-3 pt-2 min-w-0">
            <div
              className="border-2 border-dashed rounded-xl aspect-square flex items-center justify-center cursor-pointer overflow-hidden bg-muted/40"
              onClick={() => fileRef.current?.click()}
              data-testid="create-post-photo-drop"
            >
              {photoPreview
                ? <img src={photoPreview} alt="preview" className="w-full h-full object-cover" style={{ filter: photoFilter.css === 'none' ? undefined : scaleFilter(photoFilter.css, filterStrength) }} />
                : <div className="text-center text-muted-foreground p-6"><ImagePlus className="w-10 h-10 mx-auto mb-2" /><p className="text-sm">{es ? 'Toca para subir una foto' : 'Tap to upload a photo'}</p></div>}
            </div>
            {photoPreview && (
              <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 min-w-0 w-full max-w-full" data-testid="create-post-filters">
                {PHOTO_FILTERS.map((f) => (
                  <button key={f.id} onClick={() => { setPhotoFilter(f); setFilterStrength(100); }}
                    className={`shrink-0 flex flex-col items-center gap-1 ${photoFilter.id === f.id ? 'opacity-100' : 'opacity-70'}`}
                    data-testid={`create-post-filter-${f.id}`}>
                    <span className={`w-14 h-14 rounded-lg overflow-hidden border-2 ${photoFilter.id === f.id ? 'border-primary' : 'border-transparent'}`}>
                      <img src={photoPreview} alt={f.name} className="w-full h-full object-cover" style={{ filter: f.css === 'none' ? undefined : f.css }} />
                    </span>
                    <span className="text-[10px]">{f.name}</span>
                  </button>
                ))}
              </div>
            )}
            {photoPreview && photoFilter.id !== 'normal' && (
              <div className="flex items-center gap-3 px-1" data-testid="create-post-filter-strength">
                <span className="text-xs text-muted-foreground shrink-0">{es ? 'Intensidad' : 'Strength'}</span>
                <Slider value={[filterStrength]} onValueChange={(v) => setFilterStrength(v[0])} min={0} max={100} step={5} className="flex-1" />
                <span className="text-xs text-muted-foreground w-8 text-right">{filterStrength}%</span>
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="create-post-file-input" />
          </TabsContent>

          <TabsContent value="verse" className="space-y-3 pt-2">
            <img ref={previewCanvasRef} alt="verse preview" className="w-full aspect-square object-cover rounded-xl bg-muted" data-testid="create-post-verse-preview" />
            <div className="space-y-2">
              <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder={es ? 'Referencia (ej. Juan 3:16)' : 'Reference (e.g. John 3:16)'} data-testid="create-post-verse-ref" />
              <Textarea value={verseText} onChange={(e) => setVerseText(e.target.value)} placeholder={es ? 'Texto del versículo…' : 'Verse text…'} rows={3} data-testid="create-post-verse-text" />
              <GradientPicker gradient={gradient} setGradient={setGradient} />
            </div>
          </TabsContent>

          <TabsContent value="text" className="space-y-3 pt-2">
            <img ref={isTextual && tab === 'text' ? previewCanvasRef : null} alt="text preview" className="w-full aspect-square object-cover rounded-xl bg-muted" data-testid="create-post-text-preview" />
            <div className="space-y-2">
              <Textarea value={verseText} onChange={(e) => setVerseText(e.target.value)} placeholder={es ? 'Escribe algo…' : 'Write something…'} rows={4} data-testid="create-post-text-input" />
              <GradientPicker gradient={gradient} setGradient={setGradient} />
            </div>
          </TabsContent>
        </Tabs>

        <div className="space-y-3 pt-1">
          {!isStory && (
            <Textarea value={caption} onChange={(e) => setCaption(e.target.value)} placeholder={es ? 'Escribe una descripción…' : 'Write a caption…'} rows={2} data-testid="create-post-caption" />
          )}
          <div>
            <Label className="text-xs text-muted-foreground">{es ? 'Visibilidad' : 'Visibility'}</Label>
            <Select value={visibility} onValueChange={setVisibility}>
              <SelectTrigger data-testid="create-post-visibility"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="public"><span className="flex items-center gap-2"><Globe className="w-4 h-4" /> {es ? 'Público' : 'Public'}</span></SelectItem>
                <SelectItem value="friends"><span className="flex items-center gap-2"><Users className="w-4 h-4" /> {es ? 'Solo amigos' : 'Friends only'}</span></SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button className="w-full" onClick={submit} disabled={submitting} data-testid="create-post-submit">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {isStory ? (es ? 'Publicar historia' : 'Share story') : (es ? 'Publicar' : 'Share post')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const GradientPicker = ({ gradient, setGradient }) => (
  <div className="flex gap-2 flex-wrap">
    {VERSE_GRADIENTS.map((g) => (
      <button key={g.id} onClick={() => setGradient(g)} title={g.name}
        className={`w-8 h-8 rounded-full border-2 ${gradient.id === g.id ? 'border-foreground scale-110' : 'border-transparent'} transition-transform`}
        style={{ background: `linear-gradient(135deg, ${g.from}, ${g.to})` }}
        data-testid={`create-post-gradient-${g.id}`} />
    ))}
  </div>
);

export default CreatePostModal;
